// frontend/src/pages/admin/AdminDashboardPage.tsx
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  FileText,
  Users,
  ShoppingBag,
  BarChart3,
  Settings,
  BookOpen,
  Tag as TagIconLucide,
  MessageSquareText,
  FileWarning, 
  CheckCircle2, 
  Edit3,        // For Edit About Us
  Home,         // For Edit Homepage
  PlusCircle,   // For Create actions
  type LucideProps,
  LogOut,
  Loader2,
} from "lucide-react";

// API Service imports
// CORRECTED: Import the new count functions
import { getArticlesCount } from "@/api/articleService";
import { getResearchItemsCount } from "@/api/researchService";
import { getUsersCount } from "@/api/userService";
import { getProductsCount } from "@/api/productService";
import { getAllTags } from "@/api/tagService";
import { getContactSubmissionsCount } from "@/api/contactService";
import { type ContactStatus } from "@/types"; 

interface OverviewStat {
  title: string;
  value: string | number; // Allow number for direct count
  icon: React.FC<LucideProps>;
  link?: string; 
  isLoading: boolean;
  statusQuery?: ContactStatus | ""; 
}

interface ActionItem {
  label: string;
  link: string;
  icon: React.FC<LucideProps>;
  disabled?: boolean;
}

interface ActionSection {
  title: string;
  description: string;
  actions: ActionItem[];
}

const initialOverviewStats: OverviewStat[] = [
  {
    title: "Total Articles",
    value: "...",
    icon: FileText,
    link: "/admin/articles",
    isLoading: true,
  },
  {
    title: "Total Research",
    value: "...",
    icon: BookOpen,
    link: "/admin/research",
    isLoading: true,
  },
  {
    title: "Unreviewed Submissions",
    value: "...",
    icon: FileWarning,
    link: "/admin/contact-submissions",
    statusQuery: "UNREVIEWED", 
    isLoading: true,
  },
  {
    title: "Completed Submissions", 
    value: "...",
    icon: CheckCircle2,
    link: "/admin/contact-submissions",
    statusQuery: "COMPLETED", 
    isLoading: true,
  },
  {
    title: "Total Submissions",
    value: "...",
    icon: MessageSquareText,
    link: "/admin/contact-submissions",
    statusQuery: "", 
    isLoading: true,
  },
  {
    title: "Registered Users",
    value: "...",
    icon: Users,
    link: "/admin/users",
    isLoading: true,
  },
  {
    title: "Listed Products",
    value: "...",
    icon: ShoppingBag,
    link: "/admin/products",
    isLoading: true,
  },
    {
    title: "Total Tags",
    value: "...",
    icon: TagIconLucide,
    link: "/admin/tags",
    isLoading: true,
  },
];

const quickActionsList: ActionItem[] = [
  { label: "New Article", link: "/admin/articles/new", icon: PlusCircle },
  { label: "New Product", link: "/admin/products/new", icon: PlusCircle },
  { label: "New Research", link: "/admin/research/new", icon: PlusCircle },
  { label: "New User", link: "/admin/users/new", icon: PlusCircle },
];

const actionSections: ActionSection[] = [
  {
    title: "Content Management",
    description: "Manage articles, research, products, and tags.",
    actions: [
      { label: "Manage Articles", link: "/admin/articles", icon: FileText },
      { label: "Manage Research", link: "/admin/research", icon: BookOpen },
      { label: "Manage Products", link: "/admin/products", icon: ShoppingBag },
      { label: "Manage Tags", link: "/admin/tags", icon: TagIconLucide },
    ],
  },
  {
    title: "Site Customization",
    description: "Edit homepage content, about us page, and global site settings.",
    actions: [
        { label: "Edit Homepage", link: "/admin/content/homepage", icon: Home },
        { label: "Edit About Us Page", link: "/admin/content/about-us", icon: Edit3 },
        { label: "Site Settings", link: "/admin/settings", icon: Settings },
    ]
  },
  {
    title: "User & System",
    description: "Administer users, view submissions, and check analytics.",
    actions: [
      { label: "Manage Users", link: "/admin/users", icon: Users },
      { 
        label: "Manage Submissions",
        link: "/admin/contact-submissions",
        icon: MessageSquareText, 
      },
      {
        label: "View Analytics",
        link: "/admin/analytics",
        icon: BarChart3, 
      },
    ],
  },
];


const AdminDashboardPage = () => {
  const adminName = localStorage.getItem('userName') || "Admin";
  const navigate = useNavigate();

  const [overviewStats, setOverviewStats] = useState<OverviewStat[]>(initialOverviewStats);

  const extractCount = (data: any): number => {
    if (typeof data?.total === 'number') return data.total; 
    if (Array.isArray(data)) return data.length; 
    return 0;
  };

  useEffect(() => {
    const fetchCounts = async () => {
      setOverviewStats(prev => prev.map(s => ({ ...s, isLoading: true, value: "..." })));
      try {
        console.log("Attempting to fetch dashboard counts...");
        
        // CORRECTED: Call the efficient count endpoints
        const promises = [
          getArticlesCount(),
          getResearchItemsCount(),
          getUsersCount(),
          getProductsCount(),
          getAllTags(), // getAllTags is fine as the number of tags is usually small
          getContactSubmissionsCount({ status: "" as ContactStatus | "" }), 
          getContactSubmissionsCount({ status: "UNREVIEWED" }), 
          getContactSubmissionsCount({ status: "COMPLETED" }), 
        ];

        const results = await Promise.allSettled(promises);

        const [
          articlesData,
          researchData,
          usersData,
          productsData,
          tagsData,
          totalSubmissionsData,
          unreviewedSubmissionsData,
          completedSubmissionsData,
        ] = results.map(result => result.status === 'fulfilled' ? result.value : { total: "Error" });

        console.log("Fetched data for dashboard (Promise.allSettled results mapped):", {
            articlesData, researchData, usersData, productsData, tagsData,
            totalSubmissionsData, unreviewedSubmissionsData, completedSubmissionsData
        });

        setOverviewStats((prevStats) =>
          prevStats.map((stat) => {
            let newValue: string | number = "Error"; 
            let dataToUse: any;

            switch (stat.title) {
              case "Total Articles": dataToUse = articlesData; break;
              case "Total Research": dataToUse = researchData; break;
              case "Total Tags": dataToUse = tagsData; break;
              case "Registered Users": dataToUse = usersData; break;
              case "Listed Products": dataToUse = productsData; break;
              case "Total Submissions": dataToUse = totalSubmissionsData; break;
              case "Unreviewed Submissions": dataToUse = unreviewedSubmissionsData; break;
              case "Completed Submissions": dataToUse = completedSubmissionsData; break;
              default: return { ...stat, isLoading: false }; 
            }
            
            if (dataToUse && dataToUse.total !== "Error") {
                newValue = String(extractCount(dataToUse));
            }
            return { ...stat, value: newValue, isLoading: false };
          })
        );
      } catch (error: any) { 
        console.error("General error in fetchCounts:", error);
        setOverviewStats((prevStats) =>
          prevStats.map((stat) => ({ ...stat, value: "Error", isLoading: false }))
        );
      }
    };

    fetchCounts();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    navigate("/login");
  };
  
  const handleMetricCardClick = (link?: string, statusQuery?: ContactStatus | "") => {
    if (link) {
      if (statusQuery !== undefined) { 
        navigate(`${link}?status=${statusQuery}`);
      } else {
        navigate(link);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-10 text-foreground bg-background">
      <header className="border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center">
            <LayoutDashboard className="h-10 w-10 md:h-12 md:w-12 text-primary mr-3 md:mr-4 flex-shrink-0" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Admin <span className="text-gradient-maroon-to-gold">Dashboard</span>
              </h1>
              <p className="mt-1 md:mt-2 text-md md:text-lg text-muted-foreground">
                Welcome back, {adminName}! Manage your Vedas Trading platform.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive transition-colors self-start sm:self-auto mt-3 sm:mt-0 cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>
      </header>

      <section>
        <h2 className="text-2xl font-semibold mb-6 text-foreground">Site Overview</h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"> 
          {overviewStats.map((stat) => {
            const StatIcon = stat.icon;
            const linkTarget = stat.link 
              ? stat.statusQuery !== undefined 
                ? `${stat.link}?status=${stat.statusQuery}` 
                : stat.link
              : undefined;
            const isClickable = !!linkTarget;

            return (
              <Card
                key={stat.title}
                className={`group bg-card text-card-foreground shadow-lg hover:shadow-xl dark:hover:shadow-primary/10 transition-all duration-300 ease-in-out border border-border/30 hover:border-primary/30 relative overflow-hidden ${isClickable ? 'cursor-pointer' : ''}`}
                onClick={isClickable ? () => handleMetricCardClick(stat.link, stat.statusQuery) : undefined}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/80 via-primary/80 to-accent/80 scale-x-0 group-hover:scale-x-100 transition-transform duration-400 ease-out origin-center"></div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 bg-gradient-to-br from-primary/5 via-background to-accent/5 dark:from-primary/10 dark:via-card dark:to-accent/10"></div>
                <div className="relative z-10 p-4">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-0 mb-1">
                    <CardTitle className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                      {stat.title}
                    </CardTitle>
                    <StatIcon className="h-5 w-5 text-primary group-hover:text-accent transition-colors" />
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {stat.isLoading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : stat.value}
                    </div>
                    {linkTarget && !stat.isLoading && (
                        <Link
                        to={linkTarget}
                        className="text-xs text-primary hover:text-accent hover:underline transition-colors dark:text-accent dark:hover:text-primary mt-1 block"
                        onClick={(e) => e.stopPropagation()} 
                        >
                        View Details &rarr;
                        </Link>
                    )}
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-6 mt-10 text-foreground">Management Sections</h2>
        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {actionSections.map((section) => (
            <Card
              key={section.title}
              className="bg-card text-card-foreground shadow-lg border border-border/30 hover:shadow-xl transition-shadow dark:hover:shadow-primary/10"
            >
              <CardHeader>
                <CardTitle className="text-xl text-foreground">
                  {section.title}
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  {section.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {section.actions.map((action) => {
                  const ActionIcon = action.icon;
                  return (
                    <Button
                      key={action.label}
                      variant="outline"
                      className={`w-full justify-start border-border hover:border-primary hover:bg-primary/5 group transition-colors ${action.disabled ? "opacity-50 cursor-not-allowed" : "text-foreground hover:text-primary dark:hover:text-accent"}`}
                      asChild={!action.disabled}
                      disabled={action.disabled}
                    >
                      {action.disabled ? (
                        <>
                          <ActionIcon className="mr-3 h-5 w-5 text-muted-foreground" />{" "}
                          {action.label}
                        </>
                      ) : (
                        <Link to={action.link}>
                          <ActionIcon className="mr-3 h-5 w-5 text-primary group-hover:text-accent transition-colors" />{" "}
                          {action.label}
                        </Link>
                      )}
                    </Button>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-6 mt-10 text-foreground">
          Common Tasks
        </h2>
        <div className="flex flex-wrap gap-4">
          {quickActionsList.map((action) => {
            const ActionIcon = action.icon;
            return (
              <Button
                key={action.label}
                variant="default"
                className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all"
                asChild
              >
                <Link to={action.link}>
                  <ActionIcon className="mr-2 h-4 w-4" /> {action.label}
                </Link>
              </Button>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboardPage;
